var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import * as React from 'react';
import * as Sentry from '@sentry/browser';
import _get from 'lodash/get';
import { setConfiguration as setGridConfiguration } from 'react-grid-system';
import { translate } from 'react-i18next';
import { observer, inject } from 'mobx-react';
import { reaction } from 'mobx';
import { withRouter } from 'react-router';
import MainContent from './components/containers/MainContent/MainContent';
import PageContext from './context/PageContext';
import AppHelmet from './components/containers/AppHelmet/AppHelmet';
import { ModalOverlay } from './components/modals';
import Notification from './components/ui/Notification/Notification';
import ScrollToTop from './components/ui/ScrollToTop/ScrollToTop';
import { CookieModal } from '@bytebrand/fe-ui-core-autobahn/compat';
import { setUtmParameters, setLandingContext } from 'framework/utils/CommonUtils';
import Validator from './framework/Validator';
import GoogleTagManager from './services/GoogleTagManager';
import ProfileAPI from './services/ProfileAPI';
import { TopBar, Footer } from '@bytebrand/fe-ui-core-autobahn/chrome';
import { Button } from '@bytebrand/fe-ui-core-autobahn/ui';
import styles from './App.styl';
// Every top-level path AppRouter's <Switch> matches before its final catch-all NotFoundPage
// route (keep in sync with AppRouter.tsx's <Route path=…> list). Anything NOT starting with one
// of these (and not '/' itself, handled separately below) falls through to the 404 page, which
// wants the same full-width md3 layout as the homepage — not MainContent's legacy 1100px cap
// (see isSpaLayout in App's render()).
const KNOWN_ROUTE_PREFIXES = [
    '/search-next', '/favourites-next', '/login-next', '/checkout-next',
    '/login', '/dealer/sign-up', '/search', '/alternative', '/favourites', '/compare',
    '/my-settings', '/my-vehicles', '/my-searches', '/checkout', '/seller/', '/account', '/image-add',
];
Validator();
// Configure grid global column paddings. Default values is 30.
// But 30 is too much for us. Let's set 15px
setGridConfiguration({ gutterWidth: 15 });
// All global styles imported here. E.g slick-theme, rc-select, day-picker, etc.
// To make any *.styl file bundled without processing through css-modules, just add .global
// before extension. YourExampleFilename.global.styl
import './App.global.styl';
/** "First Last" from a user profile's private address, '' when it has none. */
const profileName = (profile) => {
    const { firstName = '', lastName = '' } = _get(profile, 'privateAddress') || {};
    return `${firstName} ${lastName}`.trim();
};
let App = class App extends React.Component {
    constructor(props) {
        super(props);
        Object.defineProperty(this, "disposeSavedSearchesReaction", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // Signature kept for the many Page/changePageContext callers; the third (showCrumbs)
        // argument is ignored — the md3 design has no breadcrumb bar.
        Object.defineProperty(this, "changePageContext", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (page, props, _showCrumbs, mainContentClass) => {
                this.setState(() => ({
                    page,
                    mainContentClass,
                    pageProps: props
                }));
            }
        });
        Object.defineProperty(this, "onLogOutClick", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const { financingStore } = this.props;
                financingStore.clearRequestedOffer();
                return this.props.authStore.signOut();
            }
        });
        Object.defineProperty(this, "setMainDealer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (dealerId) => {
                const { profileStore, authStore } = this.props;
                profileStore.switchDealer(dealerId)
                    .then(() => {
                    return authStore.updateToken(true);
                })
                    .then(() => {
                    authStore.setShouldLoadCars(true);
                });
            }
        });
        this.state = {
            page: 'DEFAULT',
            pageProps: {},
            mainContentClass: '',
            changePageContext: this.changePageContext,
            accountName: ''
        };
    }
    // ADV-285: with componentDidCatch alone, React renders this boundary's children as null, so
    // any render error outside a page's own AutobahnErrorBoundary (TopBar, Footer, toasts,
    // modals, CookieModal) left the whole site as a blank white page. Render a fallback instead.
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch(error, errorInfo) {
        Sentry.withScope((scope) => {
            Object.keys(errorInfo).forEach((key) => {
                scope.setExtra(key, errorInfo[key]);
            });
            Sentry.captureException(error);
        });
    }
    componentDidMount() {
        GoogleTagManager.init();
        setUtmParameters();
        setLandingContext();
        // Post-cutover: the dataLayer variant dimension is kept (hardcoded 'next') so the
        // GTM funnel comparison against the pre-cutover 'legacy' baseline stays possible
        // during the bake. Remove together with the dimension in GTM once the bake is over.
        GoogleTagManager.pushEvent({ autobahnVariant: 'next' });
        // Konto menu, on every page once signed in: the saved searches for the "Gespeicherte Suchen"
        // badge, and the profile's name for the head (profileStore loads the profile on a few pages
        // only — fetched here directly so its loading/error state stays untouched).
        this.disposeSavedSearchesReaction = reaction(() => this.props.authStore.isAuthorized, (isAuthorized) => {
            if (!isAuthorized) {
                if (this.state.accountName)
                    this.setState({ accountName: '' });
                return;
            }
            this.props.filterStore.getSavedSearchStrings(true);
            ProfileAPI.getUserProfileData()
                .then((profile) => this.setState({ accountName: profileName(profile) }))
                .catch(() => undefined);
        }, { fireImmediately: true });
    }
    componentWillUnmount() {
        if (this.disposeSavedSearchesReaction)
            this.disposeSavedSearchesReaction();
    }
    render() {
        if (this.state.hasError) {
            const { t } = this.props;
            // a full reload rather than a re-render: the crashed shell state is not trustworthy
            return (React.createElement("div", { role: "alert", className: `${styles.app} md3`, style: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '64px 24px', textAlign: 'center', minHeight: '60vh' } },
                React.createElement("p", { className: "md-title-lg", style: { margin: 0 } }, t('autobahn:errorBoundary.title')),
                React.createElement("p", { className: "md-body-md", style: { margin: 0, color: 'var(--md-on-surface-variant)', maxWidth: 420 } }, t('autobahn:errorBoundary.message')),
                React.createElement(Button, { variant: "filled", onClick: () => window.location.reload() }, t('autobahn:errorBoundary.retry'))));
        }
        const { children, t, authStore, favoriteStore, profileStore, appStore, filterStore, compareStore, location } = this.props;
        const { isAuthorized, userEmail, isDealer, isDealerMain, isDealerAdmin, isEmployee } = authStore;
        const { dealers, totalOrdered } = profileStore;
        const { language, handleSentryInit } = appStore;
        const amountOfFavorite = favoriteStore.favoritesIDs.length;
        // Konto menu head: the profile's name, as in Figma (the store's copy wins once a page loaded or
        // edited it) — the e-mail until a name is known
        const userName = profileName(profileStore.userProfile) || this.state.accountName;
        const isCheckout = location.pathname.includes('/checkout');
        // /my-vehicles/:vehicleID (exact — NOT /overview, /add, /edit or the :id/edit,
        // :id/image-upload sub-routes, which stay on the legacy MainContent layout) renders the
        // same md3 VehicleDetailedNextPage as /search/vehicle/:id and needs the same bypass below,
        // otherwise MainContent's container() mixin caps it at max-width: 1100px.
        const myVehiclesSegments = location.pathname.split('/').filter(Boolean);
        const isMyVehicleDetail = myVehiclesSegments[0] === 'my-vehicles' &&
            myVehiclesSegments.length === 2 &&
            !['overview', 'add', 'edit'].includes(myVehiclesSegments[1]);
        // unmatched routes fall through AppRouter's Switch to the catch-all NotFoundPage — same
        // full-width md3 layout as the homepage, not the legacy MainContent wrapper.
        const isKnownRoute = location.pathname === '/' ||
            KNOWN_ROUTE_PREFIXES.some((prefix) => location.pathname === prefix || location.pathname.startsWith(`${prefix.replace(/\/$/, '')}/`));
        const is404 = !isKnownRoute;
        // SPA routes that own their body layout (bypass the MainContent breadcrumb wrapper).
        const isSpaLayout = location.pathname.startsWith('/search') ||
            location.pathname.startsWith('/alternative') ||
            location.pathname.startsWith('/favourites') ||
            location.pathname === '/' ||
            isMyVehicleDetail ||
            is404;
        // Mobile header variant, per the "Header-System" spec (page-type → variant table):
        //   Conversion — CDP (car detail pages), Request-Process, Checkout
        //   Browsing   — SRP, Make/Model listing pages, Favorite-close usage
        //   Default    — Homepage, Landingpages, Magazin, general content pages
        // Checkout itself never reaches this — TopBar is already skipped entirely there (see
        // `!isCheckout` below), so "Checkout ⇒ Conversion" is moot in fe-main specifically.
        // Magazin/Landingpages aren't fe-main SPA routes (WordPress/fe-landing own those); the
        // homepage ('/') IS fe-main now (see isSpaLayout above), so "Default" here is fe-main's own
        // fallback — homepage, account/settings pages, etc. — for whatever isn't a CDP or browsing
        // route below.
        const altSegments = location.pathname.split('/').filter(Boolean);
        const isAlternativeDetail = altSegments[0] === 'alternative' &&
            altSegments.length === 2 &&
            altSegments[1] !== 'detailed';
        // CDP: a single car's detail page, wherever it's reached from.
        const isCarDetailPage = location.pathname.startsWith('/search/vehicle/') ||
            location.pathname.startsWith('/alternative/vehicle/') ||
            isAlternativeDetail ||
            isMyVehicleDetail;
        // SRP / listing / favourites — "browsing" the car list, not looking at one specific car.
        const isBrowsingPage = !isCarDetailPage && (location.pathname.startsWith('/search') ||
            location.pathname.startsWith('/alternative') ||
            location.pathname.startsWith('/favourites'));
        const topBarVariant = isCarDetailPage ? 'conversion' : isBrowsingPage ? 'browsing' : 'default';
        // homepage-only 'default' page that also gets the scroll-hide header — see TopBar's
        // autoHideOnScroll doc comment. Not every 'default' page (e.g. account/settings stays
        // docked) — only '/' itself, matching what was actually asked for.
        const isHomePage = location.pathname === '/';
        // SRP list page opts in too — NOT the whole 'browsing' variant (/alternative, /favourites
        // stay docked; only /search itself was asked for). isCarDetailPage already excludes
        // /search/vehicle/:id, which gets this via topBarVariant === 'conversion' below anyway.
        const isSearchPage = location.pathname.startsWith('/search') && !isCarDetailPage;
        return (React.createElement("div", { className: `${styles.app} md3` },
            React.createElement(PageContext.Provider, { value: this.state },
                !isSpaLayout && !isCheckout && (React.createElement(AppHelmet, { page: this.state.page, pageProps: this.state.pageProps, lng: language })),
                React.createElement(ScrollToTop, null),
                !isCheckout && (React.createElement(TopBar, { t: t, variant: topBarVariant, autoHideOnScroll: topBarVariant === 'conversion' || isHomePage || isSearchPage, isAuthorized: isAuthorized, userLabel: userName || userEmail, favoritesCount: amountOfFavorite, ordersCount: totalOrdered, savedSearchesCount: filterStore.savedSearchStrings.length, compareCount: compareStore.vehiclesIDsToCompare.length, language: language, isDealer: !!isDealer, isDealerMain: !!isDealerMain, isDealerAdmin: !!isDealerAdmin, isEmployee: !!isEmployee, dealers: dealers, onDealerSelect: this.setMainDealer, onSignOut: this.onLogOutClick, onLangSelect: (lang) => appStore.changeLang(lang), onNavigate: (to) => this.props.history.push(to) })),
                isSpaLayout ? children : (React.createElement(MainContent, { page: this.state.page, pageProps: this.state.pageProps, mainContentClass: this.state.mainContentClass }, children)),
                !isCheckout && React.createElement(Footer, { t: t, showCostFootnotes: isCarDetailPage }),
                !isCheckout && React.createElement(ModalOverlay, { location: location }),
                React.createElement(Notification, null),
                React.createElement(CookieModal, { handleSentryInit: handleSentryInit, t: t }))));
    }
};
App = __decorate([
    inject('authStore', 'profileStore', 'appStore', 'favoriteStore', 'financingStore', 'carStore', 'filterStore', 'compareStore'),
    observer,
    __metadata("design:paramtypes", [Object])
], App);
export default withRouter(translate(['header', 'footer', 'SearchPage', 'Notifications', 'autobahn'])(App));
